import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Play, CheckCircle2, Package, Calendar, User, AlertTriangle, FileText, BookOpenCheck, Image, Layers, ZoomIn, ClipboardCheck, Edit, Box, MoreVertical, Clock, Snowflake, QrCode } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PlybookExecutionView, PlyImageOverlay } from "@/components/Plybook";
import { usePlybookPlies } from "@/hooks/usePlybooks";
import { QualityInspectionPanel } from "@/components/Quality";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { format, isBefore, addDays } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useState } from "react";
import { AddProcessDialog } from "./AddProcessDialog";
import { ProcessTimeline } from "./ProcessTimeline";
import { EditWorkOrderDialog } from "./EditWorkOrderDialog";
import { toast } from "sonner";
import { CreateProductionBatchDialog } from "./CreateProductionBatchDialog";
import { Progress } from "@/components/ui/progress";
import { DeliveryNoteDialog } from "@/components/DeliveryNote";
import { PrintLabelDialog } from "@/components/Labels/PrintLabelDialog";
import type { DeliveryNote } from "@/types/deliveryNote";
import { DEFAULT_COMPANY, formatCurrency } from "@/types/deliveryNote";

interface WorkOrderDetailsProps {
  orderId: string;
  onBack: () => void;
}

export function WorkOrderDetails({ orderId, onBack }: WorkOrderDetailsProps) {
  const { t, language } = useLanguage();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();
  const [addProcessOpen, setAddProcessOpen] = useState(false);
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [deliveryNoteOpen, setDeliveryNoteOpen] = useState(false);
  const [editingDeliveryNote, setEditingDeliveryNote] = useState<DeliveryNote | null>(null);
  const [editingDeliveryNoteId, setEditingDeliveryNoteId] = useState<string | null>(null);
  const [deliveryNoteLoading, setDeliveryNoteLoading] = useState(false);
  const [editOrderOpen, setEditOrderOpen] = useState(false);
  const [showFinalLabelDialog, setShowFinalLabelDialog] = useState(false);

  // Roles that can edit work orders
  const canEditWorkOrder = ['superadmin', 'admin', 'supervisor', 'plotter'].includes(userRole || '');

  const { data: order } = useQuery({
    queryKey: ["work-order", orderId],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select(`
          *,
          customers(id, name, code, email, phone),
          creator:profiles!work_orders_created_by_fkey(full_name, email),
          plybook:plybooks(id, name, code, total_plies, status, main_image_url, pdf_file_url)
        `)
        .eq("id", orderId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: processRecords } = useQuery({
    queryKey: ["process-records", orderId],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_records")
        .select(
          `
          *,
          manufacturing_processes(*),
          profiles(*)
        `
        )
        .eq("work_order_id", orderId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!user && !!orderId,
  });

  const { data: productionBatches } = useQuery({
    queryKey: ["production-batches", orderId],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_batches")
        .select(
          `
          *,
          production_recipes(name),
          molds(name, code, current_uses, max_uses),
          machines(name, model)
        `
        )
        .eq("work_order_id", orderId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user && !!orderId,
  });

  const { data: deliveryNotes } = useQuery({
    queryKey: ["delivery-notes", orderId],
    queryFn: async () => {
      const { data, error } = await api
        .from("delivery_notes")
        .select("*")
        .eq("work_order_id", orderId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user && !!orderId,
  });

  // Query para piezas individuales
  const { data: workOrderItems } = useQuery({
    queryKey: ["work-order-items", orderId],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_order_items")
        .select(`*, profiles:assigned_operator_id(id, full_name)`)
        .eq("work_order_id", orderId)
        .order("item_number", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!orderId,
  });

  // Query para paquete vinculado (out-time tracking)
  const { data: linkedPackage } = useQuery({
    queryKey: ["linked-package", order?.linked_plotter_cut_id],
    queryFn: async () => {
      const { data, error } = await api
        .from("plotter_cuts")
        .select("id, package_code, removed_from_freezer_at, max_out_time_minutes, is_out_time_expired")
        .eq("id", order?.linked_plotter_cut_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!order?.linked_plotter_cut_id,
  });

  const { data: plybookPlies } = usePlybookPlies(order?.plybook_id);

  const handleNewDeliveryNote = () => {
    setEditingDeliveryNote(null);
    setEditingDeliveryNoteId(null);
    setDeliveryNoteOpen(true);
  };

  const handleOpenDeliveryNote = async (noteId: string) => {
    try {
      setDeliveryNoteLoading(true);
      const { data: note, error } = await api
        .from("delivery_notes")
        .select("*")
        .eq("id", noteId)
        .single();

      if (error) throw error;

      const { data: items, error: itemsError } = await api
        .from("delivery_note_items")
        .select("*")
        .eq("delivery_note_id", noteId)
        .order("line_number", { ascending: true });

      if (itemsError) throw itemsError;

      const fallbackCustomer = order?.customers
        ? {
            name: order.customers.name || "",
            cif: order.customers.code || "",
            address: "",
            city: "",
            province: "",
            postalCode: "",
          }
        : {
            name: "",
            cif: "",
            address: "",
            city: "",
            province: "",
            postalCode: "",
          };

      const deliveryNote: DeliveryNote = {
        documentType: "Albarán",
        documentNumber: note?.document_number || "",
        page: 1,
        totalPages: 1,
        date: note?.document_date || format(new Date(), "dd/MM/yyyy"),
        paymentMethod: note?.payment_method || "Transferencia bancaria",
        company: note?.company || DEFAULT_COMPANY,
        customer: note?.customer || fallbackCustomer,
        items: (items || []).map((item: any, index: number) => ({
          id: item.id || `item-${index}`,
          article: item.article || "",
          description: item.description || "",
          quantity: Number(item.quantity) || 0,
          unit: item.unit || "ud",
          unitPrice: Number(item.unit_price) || 0,
          discount: Number(item.discount) || 0,
          subtotal: Number(item.subtotal) || 0,
          total: Number(item.total) || 0,
        })),
        taxes: Array.isArray(note?.taxes) ? note.taxes : [],
        subtotal: Number(note?.subtotal) || 0,
        totalDiscount: Number(note?.total_discount) || 0,
        totalTax: Number(note?.total_tax) || 0,
        total: Number(note?.total) || 0,
        observations: note?.observations || "",
        workOrderId: note?.work_order_id || orderId,
        workOrderNumber: note?.work_order_number || order?.order_number,
        hideValues: note?.hide_values === true,
      };

      setEditingDeliveryNote(deliveryNote);
      setEditingDeliveryNoteId(note?.id || noteId);
      setDeliveryNoteOpen(true);
    } catch (error: any) {
      toast.error(error.message || "Error al cargar el albaran");
    } finally {
      setDeliveryNoteLoading(false);
    }
  };

  const handleStartOrder = async () => {
    try {
      const { error } = await api
        .from("work_orders")
        .update({
          status: "in_progress",
          started_at: new Date().toISOString(),
        })
        .eq("id", orderId);

      if (error) throw error;

      toast.success(t.common.success);
      queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCompleteOrder = async () => {
    try {
      const { error } = await api
        .from("work_orders")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", orderId);

      if (error) throw error;

      toast.success(t.common.success);
      queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  // Funciones para actualizar estado de piezas
  const handleStartItem = async (itemId: string) => {
    try {
      const { error } = await api.from("work_order_items").update({
        status: "in_progress",
        started_at: new Date().toISOString(),
      }).eq("id", itemId);
      if (error) throw error;
      toast.success("Pieza iniciada");
      queryClient.invalidateQueries({ queryKey: ["work-order-items", orderId] });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCompleteItem = async (itemId: string) => {
    try {
      const { error } = await api.from("work_order_items").update({
        status: "completed",
        completed_at: new Date().toISOString(),
      }).eq("id", itemId);
      if (error) throw error;
      toast.success("Pieza completada");
      queryClient.invalidateQueries({ queryKey: ["work-order-items", orderId] });
      queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  // Calcular tiempo restante fuera del congelador
  const getOutTimeRemaining = () => {
    if (!linkedPackage?.removed_from_freezer_at) return null;
    const removedAt = new Date(linkedPackage.removed_from_freezer_at);
    const maxMs = (linkedPackage.max_out_time_minutes || 120) * 60 * 1000;
    const elapsed = Date.now() - removedAt.getTime();
    return Math.max(0, maxMs - elapsed);
  };

  const formatOutTime = (ms: number) => {
    if (ms <= 0) return "TIEMPO EXCEDIDO";
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m restantes`;
    return `${minutes} min restantes`;
  };

  const outTimeRemaining = getOutTimeRemaining();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "in_progress":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "cancelled":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "high":
        return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      case "low":
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
      default:
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
    }
  };

  const isOverdue = (dueDate: string | null, status: string) => {
    if (!dueDate || status === "completed") return false;
    return isBefore(new Date(dueDate), new Date());
  };

  const isNearDueDate = (dueDate: string | null) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const warning = addDays(new Date(), 2);
    return isBefore(due, warning) && !isBefore(due, new Date());
  };

  const getProcessDurationMinutes = (record: any) => {
    if (record.duration_minutes) return record.duration_minutes;
    if (!record.started_at) return 0;
    const end = record.completed_at ? new Date(record.completed_at) : new Date();
    const start = new Date(record.started_at);
    const duration = Math.round((end.getTime() - start.getTime()) / 60000);
    return Math.max(duration, 0);
  };

  const formatMinutes = (minutes: number) => {
    if (!minutes) return "-";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate progress
  const getOrderProgress = () => {
    if (!processRecords || processRecords.length === 0) return 0;
    const completed = processRecords.filter((r: any) => r.status === "completed").length;
    return Math.round((completed / processRecords.length) * 100);
  };

  if (!order) {
    return <div className="text-center py-12">{t.common.loading}</div>;
  }

  const overdue = isOverdue(order.due_date, order.status);
  const nearDue = isNearDueDate(order.due_date);
  const progress = getOrderProgress();
  const operatorCount = processRecords
    ? new Set(
        processRecords.map((record: any) => record.operator_id).filter(Boolean)
      ).size
    : 0;
  const totalProcessMinutes = processRecords
    ? processRecords.reduce(
        (sum: number, record: any) => sum + getProcessDurationMinutes(record),
        0
      )
    : 0;
  const orderElapsedMinutes = order.started_at
    ? Math.max(
        0,
        Math.round(
          ((order.completed_at ? new Date(order.completed_at) : new Date()).getTime() -
            new Date(order.started_at).getTime()) /
            60000
        )
      )
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">
              {order.order_number}
            </h1>
            {overdue && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="w-3 h-3" />
                Vencida
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1">{order.product_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Edit Button - Only for authorized roles */}
          {canEditWorkOrder && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOrderOpen(true)}
              className="gap-2"
            >
              <Edit className="w-4 h-4" />
              Editar
            </Button>
          )}
          {/* Print Final Label Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFinalLabelDialog(true)}
            className="gap-2"
          >
            <QrCode className="w-4 h-4" />
            Etiqueta QR
          </Button>
          {/* Delivery Note Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleNewDeliveryNote}
            className="gap-2"
          >
            <FileText className="w-4 h-4" />
            Generar Albarán
          </Button>
          {order.priority && order.priority !== "normal" && (
            <Badge className={getPriorityColor(order.priority)}>
              {order.priority === "urgent" ? "Urgente" :
               order.priority === "high" ? "Alta" : "Baja"}
            </Badge>
          )}
          <Badge className={getStatusColor(order.status)}>
            {t.statuses[order.status as keyof typeof t.statuses]}
          </Badge>
        </div>
      </div>

      {/* Progress Card */}
      {processRecords && processRecords.length > 0 && (
        <Card className={overdue ? "border-red-500/50" : nearDue ? "border-orange-500/50" : ""}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progreso General</span>
              <span className="text-sm text-muted-foreground">{progress}%</span>
            </div>
            <Progress value={progress} className="h-3" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{processRecords.filter((r: any) => r.status === "completed").length} de {processRecords.length} procesos completados</span>
              {order.due_date && (
                <span className={overdue ? "text-red-500 font-medium" : nearDue ? "text-orange-500" : ""}>
                  Entrega: {format(new Date(order.due_date), "PP", { locale: language === "es" ? es : enUS })}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Out-Time Alert para material de congelador */}
      {linkedPackage?.removed_from_freezer_at && outTimeRemaining !== null && (
        <Alert variant={outTimeRemaining < 600000 ? "destructive" : "default"}
               className={outTimeRemaining < 600000 ? "border-red-500 bg-red-500/10" : "border-blue-500 bg-blue-500/10"}>
          <Snowflake className="w-4 h-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              <strong>Material fuera del congelador:</strong> {linkedPackage.package_code}
            </span>
            <span className={outTimeRemaining === 0 ? "text-red-600 font-bold animate-pulse" : "font-medium"}>
              <Clock className="w-4 h-4 inline mr-1" />
              {formatOutTime(outTimeRemaining)}
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Piezas Card */}
      {workOrderItems && workOrderItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Box className="w-5 h-5" />
                Piezas
              </span>
              <Badge variant="outline">
                {workOrderItems.filter((i: any) => i.status === "completed").length}/{workOrderItems.length} completadas
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {workOrderItems.map((item: any) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-medium bg-muted px-2 py-1 rounded">
                      #{item.item_number}
                    </span>
                    {item.part_label && (
                      <span className="text-muted-foreground">{item.part_label}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.profiles?.full_name && (
                      <Badge variant="outline" className="text-xs">
                        <User className="w-3 h-3 mr-1" />
                        {item.profiles.full_name}
                      </Badge>
                    )}
                    <Badge className={getStatusColor(item.status)}>
                      {item.status === "completed" ? "Completada" :
                       item.status === "in_progress" ? "En Progreso" :
                       item.status === "on_hold" ? "En Espera" :
                       item.status === "cancelled" ? "Cancelada" : "Pendiente"}
                    </Badge>
                    {canEditWorkOrder && item.status !== "completed" && item.status !== "cancelled" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {item.status === "pending" && (
                            <DropdownMenuItem onClick={() => handleStartItem(item.id)}>
                              <Play className="w-4 h-4 mr-2" />
                              Iniciar Pieza
                            </DropdownMenuItem>
                          )}
                          {item.status === "in_progress" && (
                            <DropdownMenuItem onClick={() => handleCompleteItem(item.id)}>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Completar Pieza
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Order Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t.workOrders.orderDetails}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Customer Info */}
            {order.customers && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {t.workOrders.customer}
                </p>
                <p className="font-medium">{order.customers.name}</p>
                {order.customers.code && (
                  <p className="text-xs text-muted-foreground">Código: {order.customers.code}</p>
                )}
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t.workOrders.quantity}
              </p>
              <p className="font-medium">{order.quantity} unidades</p>
            </div>

            {/* Operarios */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Operarios involucrados
              </p>
              <p className="font-medium">{operatorCount}</p>
            </div>

            {/* Tiempo OT */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Tiempo total OT
              </p>
              <p className="font-medium">{formatMinutes(orderElapsedMinutes)}</p>
            </div>

            {/* Tiempo procesos */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Tiempo total procesos
              </p>
              <p className="font-medium">{formatMinutes(totalProcessMinutes)}</p>
            </div>

            {/* Due Date */}
            {order.due_date && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fecha de Entrega
                </p>
                <p className={`font-medium ${overdue ? "text-red-500" : nearDue ? "text-orange-500" : ""}`}>
                  {format(new Date(order.due_date), "PPP", {
                    locale: language === "es" ? es : enUS,
                  })}
                  {overdue && " (Vencida)"}
                  {nearDue && " (Próxima)"}
                </p>
              </div>
            )}

            {/* Created At */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t.workOrders.createdAt}
              </p>
              <p className="font-medium">
                {format(new Date(order.created_at), "PPp", {
                  locale: language === "es" ? es : enUS,
                })}
              </p>
              {order.creator && (
                <p className="text-xs text-muted-foreground">
                  por {order.creator.full_name || order.creator.email}
                </p>
              )}
            </div>

            {/* Started At */}
            {order.started_at && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {t.workOrders.startedAt}
                </p>
                <p className="font-medium">
                  {format(new Date(order.started_at), "PPp", {
                    locale: language === "es" ? es : enUS,
                  })}
                </p>
              </div>
            )}

            {/* Completed At */}
            {order.completed_at && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {t.workOrders.completedAt}
                </p>
                <p className="font-medium text-green-600">
                  {format(new Date(order.completed_at), "PPp", {
                    locale: language === "es" ? es : enUS,
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">Notas</p>
              <p className="text-sm bg-muted/50 p-3 rounded-lg">{order.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Notes */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Albaranes</CardTitle>
            <Button size="sm" variant="outline" onClick={handleNewDeliveryNote}>
              <FileText className="w-4 h-4 mr-2" />
              Nuevo Albarán
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {deliveryNotes && deliveryNotes.length > 0 ? (
            <div className="space-y-3">
              {deliveryNotes.map((note: any) => (
                <div
                  key={note.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{note.document_number}</p>
                      {note.hide_values && (
                        <Badge variant="secondary">Transporte</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Fecha: {note.document_date}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="font-medium">
                        {note.hide_values
                          ? "No visible"
                          : `${formatCurrency(Number(note.total || 0))} €`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenDeliveryNote(note.id)}
                      disabled={deliveryNoteLoading}
                    >
                      Abrir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-6">
              No hay albaranes registrados
            </p>
          )}
        </CardContent>
      </Card>

      {/* Process Timeline Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t.workOrders.processes}</CardTitle>
            <div className="flex gap-2">
              {order.status === "pending" && (
                <Button onClick={handleStartOrder} size="sm">
                  <Play className="w-4 h-4 mr-2" />
                  {t.workOrders.startProcess}
                </Button>
              )}
              {order.status === "in_progress" && (
                <Button onClick={handleCompleteOrder} size="sm" variant="outline" className="text-green-600 border-green-600 hover:bg-green-50">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {t.workOrders.completeProcess}
                </Button>
              )}
              {order.status !== "completed" && (
                <Button onClick={() => setAddProcessOpen(true)} size="sm">
                  {t.workOrders.addProcess}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ProcessTimeline
            records={processRecords || []}
            orderStatus={order.status}
          />
        </CardContent>
      </Card>

      {/* Plybook / Laminado Section */}
      {order.plybook_id && order.plybook && (
        <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpenCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Hoja de Laminado</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {order.plybook.name} {order.plybook.code && `(${order.plybook.code})`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="gap-1">
                  <Layers className="w-3 h-3" />
                  {order.plybook.total_plies || 0} capas
                </Badge>
                <Badge
                  className={order.plybook.status === 'approved' ? 'bg-green-500' : 'bg-yellow-500'}
                >
                  {order.plybook.status === 'approved' ? 'Aprobado' : 'Borrador'}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Imagen del Plybook - Prominente */}
            {order.plybook.main_image_url ? (
              <div className="relative group">
                <div className="rounded-lg overflow-hidden border-2 border-dashed border-primary/30 bg-black/5">
                  <img
                    src={order.plybook.main_image_url}
                    alt={`Diagrama de laminado - ${order.plybook.name}`}
                    className="w-full h-auto max-h-[400px] object-contain mx-auto"
                  />
                </div>
                <a
                  href={order.plybook.main_image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ZoomIn className="w-5 h-5" />
                </a>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/20">
                <Image className="w-12 h-12 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No hay imagen de laminado disponible
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Sube una imagen desde el editor de plybook
                </p>
              </div>
            )}

            {/* PDF si existe */}
            {order.plybook.pdf_file_url && (
              <a
                href={order.plybook.pdf_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium">Ver PDF de instrucciones</span>
              </a>
            )}

            {/* Mapa de Laminado (solo lectura) */}
            {order.plybook.main_image_url && (
              <PlyImageOverlay
                imageUrl={order.plybook.main_image_url}
                plies={plybookPlies || []}
                plybookId={order.plybook_id}
                editable={false}
              />
            )}

            {/* Vista de ejecución */}
            <PlybookExecutionView
              workOrderId={orderId}
              plybookId={order.plybook_id}
              onComplete={() => {
                queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
                queryClient.invalidateQueries({ queryKey: ["process-records", orderId] });
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Quality Inspections Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle>Inspecciones de Calidad</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Control de calidad en cualquier fase del proceso
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <QualityInspectionPanel workOrderId={orderId} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lotes de Producción</CardTitle>
            {order?.status !== "completed" && (
              <Button onClick={() => setCreateBatchOpen(true)} size="sm" variant="outline">
                <Package className="w-4 h-4 mr-2" />
                Crear Lote
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {productionBatches && productionBatches.length > 0 ? (
            <div className="space-y-3">
              {productionBatches.map((batch: any) => (
                <div
                  key={batch.id}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{batch.batch_number}</h4>
                      <p className="text-sm text-muted-foreground">
                        Cantidad: {batch.quantity}
                      </p>
                    </div>
                    <Badge
                      variant={
                        batch.status === "completed"
                          ? "default"
                          : batch.status === "in_progress"
                          ? "secondary"
                          : "outline"
                      }
                    >
                      {batch.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {batch.production_recipes && (
                      <div>
                        <span className="text-muted-foreground">Receta:</span>
                        <p className="font-medium">{batch.production_recipes.name}</p>
                      </div>
                    )}
                    {batch.molds && (
                      <div>
                        <span className="text-muted-foreground">Molde:</span>
                        <p className="font-medium">
                          {batch.molds.name}
                          {batch.molds.code && ` (${batch.molds.code})`}
                        </p>
                      </div>
                    )}
                    {batch.machines && (
                      <div>
                        <span className="text-muted-foreground">Máquina:</span>
                        <p className="font-medium">{batch.machines.name}</p>
                      </div>
                    )}
                    {batch.start_date && (
                      <div>
                        <span className="text-muted-foreground">Inicio:</span>
                        <p className="font-medium">
                          {format(new Date(batch.start_date), "PPp", {
                            locale: language === "es" ? es : enUS,
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                  {batch.notes && (
                    <p className="mt-2 text-sm text-muted-foreground">{batch.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No hay lotes de producción registrados
            </p>
          )}
        </CardContent>
      </Card>

      <AddProcessDialog
        open={addProcessOpen}
        onOpenChange={setAddProcessOpen}
        workOrderId={orderId}
      />

      <CreateProductionBatchDialog
        open={createBatchOpen}
        onOpenChange={setCreateBatchOpen}
        workOrderId={orderId}
      />

      {/* Final Traceability Label Dialog */}
      <PrintLabelDialog
        open={showFinalLabelDialog}
        onOpenChange={setShowFinalLabelDialog}
        labelType="final"
        entityId={orderId}
        entityName={order ? `${order.order_number} - ${order.product_name}` : ''}
      />

      {/* Delivery Note Dialog */}
      <DeliveryNoteDialog
        open={deliveryNoteOpen}
        onOpenChange={setDeliveryNoteOpen}
        workOrderId={orderId}
        workOrderNumber={order?.order_number}
        initialDeliveryNote={editingDeliveryNote}
        initialDeliveryNoteId={editingDeliveryNoteId}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["delivery-notes", orderId] })}
        initialCustomer={
          order?.customers
            ? {
                name: order.customers.name || '',
                cif: order.customers.code || '',
                address: '',
                city: '',
                province: '',
              }
            : undefined
        }
        initialItems={[
          {
            article: order?.order_number || '',
            description: `${order?.product_name || ''}\nCantidad: ${order?.quantity || 0} unidades`,
            quantity: order?.quantity || 0,
            unit: 'ud',
            unitPrice: 0,
            discount: 0,
          },
        ]}
      />

      {/* Edit Work Order Dialog */}
      {order && (
        <EditWorkOrderDialog
          open={editOrderOpen}
          onOpenChange={setEditOrderOpen}
          workOrder={order}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
          }}
        />
      )}
    </div>
  );
}
